import { Crop } from 'react-image-crop';

import { AdVariant } from 'types/adEditorTypes';
import { PercentCrop } from 'api/video';

// ---------------------------------------------------------------------------
// Bring-to-Life crop (SYM-5564).
//
// The FE forwards the user's focus rectangle (the editor crop, in percent) plus
// the source resource id to the BE. The BE is authoritative: it crops the
// resource to this focus and fits the target output aspect for the placement.
// The FE deliberately does NOT compute placement aspect or a default cover-crop
// — that's exactly the BE-owned semantics that made an FE-only version wrong
// (feed is 1:1 in the editor but 4:5 on output).
// ---------------------------------------------------------------------------

// A crop within this many percent of the full frame on every edge carries no
// focus information → omit it and let the BE apply its own default framing.
const FULL_FRAME_EPSILON = 0.5;

const clamp = (n: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, n));

/** Clamp a percent crop into [0,100] with x+width / y+height ≤ 100. */
export const normalizePercentCrop = (crop: Crop): PercentCrop => {
  const x = clamp(crop.x, 0, 100);
  const y = clamp(crop.y, 0, 100);
  const width = clamp(crop.width, 0, 100 - x);
  const height = clamp(crop.height, 0, 100 - y);
  return { unit: '%', x, y, width, height };
};

/** True when the crop covers (effectively) the whole image. */
export const isFullFrameCrop = (crop: PercentCrop): boolean =>
  Math.abs(crop.x) <= FULL_FRAME_EPSILON &&
  Math.abs(crop.y) <= FULL_FRAME_EPSILON &&
  Math.abs(crop.width - 100) <= FULL_FRAME_EPSILON &&
  Math.abs(crop.height - 100) <= FULL_FRAME_EPSILON;

/**
 * Structural check only — is this a percent crop made of real numbers?
 *
 * Deliberately does NOT check bounds. The editor's cover-crop math routinely
 * produces small out-of-range artifacts (a real saved crop from the app reads
 * `y: -0.174, height: 100.174`), and rejecting those outright is what made the
 * first cut of this fix a no-op: every genuine focus rectangle was thrown away
 * and Bring-to-Life fell back to the original framing. Bounds are a job for
 * `normalizePercentCrop`, which clamps them — see `getBtlCrop`.
 */
const isPercentCropShaped = (crop: Crop): boolean => {
  if (crop.unit !== '%') return false;
  const { x, y, width, height } = crop;
  return [x, y, width, height].every(Number.isFinite);
};

/** A crop that survived clamping with a non-degenerate area. */
const hasArea = (crop: PercentCrop): boolean =>
  crop.width > 0 && crop.height > 0;

/**
 * The Bring-to-Life focus rectangle to send to the BE for this variant, or null
 * when there's nothing meaningful to send (no/unusable crop, or a full-frame
 * crop that carries no focus — the BE then applies its own default framing).
 *
 * Order matters: clamp first, validate second. Validating the raw rectangle
 * rejects legitimate crops over float noise; clamping first turns that noise
 * into a valid rectangle and still discards genuinely nonsensical input
 * (e.g. `x: 200` clamps to zero width and is dropped by `hasArea`).
 */
export const getBtlCrop = (
  variant: Pick<AdVariant, 'crop'>
): PercentCrop | null => {
  const crop = variant.crop;
  if (!crop || !isPercentCropShaped(crop)) return null;

  const normalized = normalizePercentCrop(crop);
  if (!hasArea(normalized)) return null;

  return isFullFrameCrop(normalized) ? null : normalized;
};
